import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  H3,
  HorizontalScroll,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  RadioGroup,
  Radio,
  Select,
  Link,
  Button,
  RadioControl,
  RadioLabel,
} from 'next/components/ui';
import { ConditionsShape } from 'next/entities/rules';
import { selectSegmentTargetingOptions } from 'next/lib/selectors-options';
import Conditions from 'next/components/Conditions';
import { create as createSegment } from 'next/entities/segments';

import { SaveAsSegmentManager } from './SaveAsSegmentManager';
import {
  HelpText,
  SegmentTargeting,
  Targeting,
  SectionTitle,
  Section,
} from './styled';

export const Audience = ({
  conditions = { and: [] },
  segments = [],
  onSegmentCreate,
  onChange,
}) => {
  const { and, or } = conditions;
  const subConditions = and || or;
  const [firstCondition] = subConditions;
  const [setBySpecific, setBySpecificValue] = useState(false);
  let selectedMode = 'all';

  if (subConditions.length > 0) {
    selectedMode =
      subConditions.length === 1 && firstCondition.segments && !setBySpecific
        ? 'segment'
        : 'specific';
  }

  const segmentOptions = [...segments];

  if (
    firstCondition?.segments?.segment &&
    !segmentOptions.some(
      ({ value }) => value === firstCondition.segments.segment
    )
  ) {
    const {
      segments: { segment: value },
    } = firstCondition;

    segmentOptions.push({
      label: value,
      value,
    });
  }

  const handleModeChange = nextMode => {
    switch (nextMode) {
      case 'all':
        setBySpecificValue(false);
        onChange(null);
        break;
      case 'segment': {
        setBySpecificValue(false);
        const condition = { segments: {} };

        if (segments?.length > 0) {
          const [{ value: segment }] = segments;
          condition.segments.segment = segment;
        }

        onChange({
          and: [condition],
        });
        break;
      }
      case 'specific':
      default:
        setBySpecificValue(true);
        onChange({
          and: [{ properties: {} }],
        });
        break;
    }
  };

  const handleSegmentChange = ({ value: segment }) =>
    void onChange({ and: [{ segments: { segment } }] });

  const handleAddCriteriaClick = () =>
    void onChange({ and: [firstCondition, { properties: {} }] });

  const handleSegmentCreate = async name => {
    const { id: segment } = await onSegmentCreate({ name, conditions });
    onChange({ and: [{ segments: { segment } }] });
  };

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <H3>Audience targeting</H3>
        </PanelTitle>
      </PanelHeader>
      <PanelBody>
        <Section>
          <SectionTitle>
            Which users should see this Pin Experience?
          </SectionTitle>
          <RadioGroup
            name="mode"
            value={selectedMode}
            onValueChange={handleModeChange}
          >
            <RadioControl>
              <Radio id="all-users" value="all" />
              <RadioLabel htmlFor="all-users">All users</RadioLabel>
            </RadioControl>
            <RadioControl>
              <Radio id="segment" value="segment" />
              <RadioLabel htmlFor="segment">Users in a Segment</RadioLabel>
            </RadioControl>
            {selectedMode === 'segment' && (
              <Targeting>
                <SegmentTargeting>
                  <Select
                    value={segmentOptions.find(
                      ({ value }) => value === firstCondition.segments.segment
                    )}
                    options={segmentOptions}
                    onChange={handleSegmentChange}
                    placeholder="Select a segment..."
                  />
                  <Button kind="secondary" onClick={handleAddCriteriaClick}>
                    Add criteria
                  </Button>
                </SegmentTargeting>
                <HelpText>
                  You can view and manage the conditions for this Segment on the{' '}
                  <Link to="/segments">Segments page</Link>.
                </HelpText>
              </Targeting>
            )}
            <RadioControl>
              <Radio id="specific-users" value="specific" />
              <RadioLabel htmlFor="specific-users">Specific users</RadioLabel>
            </RadioControl>
            {selectedMode === 'specific' && (
              <Targeting>
                <HorizontalScroll>
                  <Conditions
                    conditions={conditions}
                    onChange={onChange}
                    validClauseTypes={[
                      'properties',
                      'groups',
                      'languages',
                      'content',
                      'checklists',
                      'event',
                      'segments',
                    ]}
                  />
                </HorizontalScroll>
                <SaveAsSegmentManager onSave={handleSegmentCreate} />
              </Targeting>
            )}
          </RadioGroup>
        </Section>
      </PanelBody>
    </Panel>
  );
};

Audience.propTypes = {
  conditions: ConditionsShape,
  segments: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ),
  onSegmentCreate: PropTypes.func,
  onChange: PropTypes.func,
};

const mapStateToProps = state => ({
  segments: selectSegmentTargetingOptions(state),
});

const mapDispatchToProps = {
  onSegmentCreate: createSegment,
};

export default connect(mapStateToProps, mapDispatchToProps)(Audience);
